import { CDSOption } from '@ciscodesignsystems/cds-react-select';
import sortBy from 'lodash/sortBy';
import _toUpper from 'lodash/toUpper';
import uniqBy from 'lodash/uniqBy';
import { createSelector } from 'reselect';
import {
  convertFeatureFlagsToSet,
  getProvisionedProductInstances,
} from './helpers';
import { IState } from '../../core/redux/interfaces';
import { EnterpriseRegionType } from '../../enums/EnterpriseType';
import { ProvisioningStatus } from '../../enums/ProvisioningStatus';
import { IProductInstance, ICommon } from '../../interfaces/ICommon';
import 'core-js/features/set';
import {
  ENTERPRISE_PRODUCT_TYPE,
  ENTERPRISE,
  FEATURE_FLAG_PREFIX,
  X_TRACE_ID_RESPONSE_HEADER,
  X_PROMETHEUS_TRACE_ID_RESPONSE_HEADER,
} from '../../utils/constant';

export const getState = (state: IState): ICommon => state.common || {};

export const getBootstrapConfig = createSelector(
  [getState],
  ({ bootstrap }) => bootstrap
);

export const getNavigation = createSelector(
  [getState],
  ({ navigation }) => navigation
);

export const getEnterpriseId = createSelector(
  [getNavigation],
  ({ enterpriseId }) => enterpriseId
);

export const getAuth = createSelector([getState], ({ auth }) => auth);

export const getAllProductsInstances = createSelector(
  [getState],
  ({ productInstances }) => {
    const SCCProductInstance = {
      productType: ENTERPRISE_PRODUCT_TYPE,
      productName: ENTERPRISE,
      provisioningStatus: ProvisioningStatus.FINISHED,
      id: ENTERPRISE_PRODUCT_TYPE,
      region: {
        name: EnterpriseRegionType.ENTERPRISE,
        description: ENTERPRISE,
      },
    };

    /* temporary sorting, so when assigning role we are sending always the same first id as tenantId.
       From BE we are getting different order for different calls. 
       Will remove when there will be only one instance per enterprise */
    const sortedProducts = sortBy(
      [...productInstances.items, SCCProductInstance],
      'id'
    );

    return {
      items:
        Array.isArray(productInstances.items) &&
        productInstances.items.length > 0
          ? sortedProducts
          : [SCCProductInstance],
    };
  }
);

export const getAllProvisionedProductsInstances = createSelector(
  [getAllProductsInstances],
  ({ items: allProductInstances }) =>
    getProvisionedProductInstances(allProductInstances)
);

export const getAllRegions = createSelector(
  [getAllProductsInstances],
  ({ items }) =>
    items.reduce(
      (
        acc: CDSOption[],
        { region: { name, description } }: IProductInstance
      ) => [...acc, { value: name, label: description }],
      []
    )
);

export const getUniqueRegions = createSelector([getAllRegions], (allRegions) =>
  uniqBy(allRegions, 'value')
);

export const getAllUsers = createSelector(
  [getState],
  ({ allUsers }) => allUsers
);

export const getIsAllUsersLoading = createSelector(
  [getState],
  ({ allUsersLoading }) => allUsersLoading
);

export const getDidAllUsersRetrieved = createSelector(
  [getState],
  ({ allUsersRetrieved }) => allUsersRetrieved
);

export const getAllRoles = createSelector(
  [getState],
  ({ allRoles }) => allRoles.items
);

export const getAllGroups = createSelector(
  [getState],
  ({ allGroups = { items: [] } }) =>
    allGroups.items.map((group) => {
      return {
        ...group,
        type: group.type || 'Local',
      };
    })
);

export const getGroupsErrorState = createSelector(
  [getState],
  ({ allGroups: { error } }) => {
    if (typeof error === 'string') {
      return {
        errorTraceId: '',
        isError: Boolean(error),
      };
    }
    const displayedTraceId =
      error?.response?.headers[X_TRACE_ID_RESPONSE_HEADER] ??
      error?.response?.headers[X_PROMETHEUS_TRACE_ID_RESPONSE_HEADER] ??
      '';
    const errorTraceId =
      error?.response?.status === 500 ? displayedTraceId : '';

    return {
      errorTraceId,
      isError: Boolean(error),
    };
  }
);

export const getCurrentUserId = createSelector(
  [getState],
  ({ auth }) => auth.userId
);

export const getFeatureFlagsSet = createSelector(
  [(state) => state.common.featureFlags],
  (currentFeatureFlags) => {
    const currentFeatureFlagsSet =
      convertFeatureFlagsToSet(currentFeatureFlags);
    return currentFeatureFlagsSet;
  }
);

export const getFeatureFlagRetrieved = createSelector(
  [getState],
  ({ featureFlagsRetrieved }) => featureFlagsRetrieved
);

export const hasFeatureFlags = createSelector(
  [getFeatureFlagsSet, (state, featureFlags: string[]) => featureFlags],
  (currentFeatureFlagsSet, flags) => {
    const uppercaseFlags = flags?.map((flag) =>
      _toUpper(flag).replace(FEATURE_FLAG_PREFIX, '')
    );
    const uppercaseFlagsSet = new Set(uppercaseFlags);
    return uppercaseFlagsSet.isSubsetOf(currentFeatureFlagsSet);
  }
);

export const getIsAllGroupsLoading = createSelector(
  [getState],
  ({ allGroups }) => allGroups.loading
);

export const getIsLoadingProductInstances = createSelector(
  [getState],
  ({ productInstances }) => productInstances.loading
);

export const getProductInstances = createSelector(
  [getState],
  ({ productInstances }) => productInstances.items
);
